import { Paper } from '@mui/material';
import { LexicalNode, NodeKey } from 'lexical';
import { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { BUILTINS, FormulaFunctionName } from '../../formula-engine';
import { FieldRefTarget } from '../../util/formula-vocabulary';
import { $isFieldChipNode } from './FieldChipNode';
import { renderFieldDetail } from './FieldTypeaheadPlugin';
import { $isFunctionNameNode } from './FunctionNamePlugin';
import { renderFunctionDetail } from './FunctionTypeaheadPlugin';

/** A documentable token node: a function name ("IF(") or a field chip. */
export type TokenRef =
  | { kind: 'function'; name: FormulaFunctionName; nodeKey: NodeKey }
  | { kind: 'field'; path: string; nodeKey: NodeKey };

/** The token a node is, if it is one — usable from a caret read or a DOM hover. */
export function $tokenAtNode(node: LexicalNode | null): TokenRef | null {
  if ($isFieldChipNode(node)) {
    return { kind: 'field', path: node.getFieldPath(), nodeKey: node.getKey() };
  }
  if ($isFunctionNameNode(node)) {
    // The entity text includes the "(" ("IF("); the name is the letters.
    const name = node
      .getTextContent()
      .replace(/[^A-Za-z]/g, '')
      .toUpperCase();
    return BUILTINS.has(name) ? { kind: 'function', name, nodeKey: node.getKey() } : null;
  }
  return null;
}

/** What a token documents: the function, or the chip's vocabulary entry. */
export type HintDoc =
  { kind: 'function'; name: FormulaFunctionName } | { kind: 'field'; target: FieldRefTarget };

/** Null for a chip whose path left the vocabulary (deleted field): nothing sane to document. */
export function resolveDoc(token: TokenRef, fields: FieldRefTarget[]): HintDoc | null {
  if (token.kind === 'function') {
    return { kind: 'function', name: token.name };
  }
  const target = fields.find((field) => field.path === token.path);
  return target ? { kind: 'field', target } : null;
}

export function renderHintDoc(doc: HintDoc): ReactNode {
  return doc.kind === 'function' ? renderFunctionDetail(doc.name) : renderFieldDetail(doc.target);
}

/**
 * A collapsed selection in an empty block has no client rect; fall back to
 * the editor box so the menu still lands somewhere sensible.
 */
export function caretRect(rootElement: HTMLElement | null): DOMRect | null {
  const domSelection = window.getSelection();
  if (domSelection && domSelection.rangeCount > 0) {
    const range = domSelection.getRangeAt(0);
    const rect = range.getClientRects()[0] ?? range.getBoundingClientRect();
    if (rect.top !== 0 || rect.left !== 0 || rect.width !== 0 || rect.height !== 0) {
      return rect;
    }
  }
  return rootElement?.getBoundingClientRect() ?? null;
}

interface DocPopupProps {
  rect: Pick<DOMRect, 'bottom' | 'left'>;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  children: ReactNode;
}

/** The typeahead's doc side panel, standalone: anchored under a token or the caret. */
export function DocPopup({ rect, onMouseEnter, onMouseLeave, children }: DocPopupProps) {
  return createPortal(
    <Paper
      elevation={4}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={{
        position: 'fixed',
        top: rect.bottom + 4,
        left: rect.left,
        minWidth: '14rem',
        maxWidth: '20rem',
        maxHeight: '18rem',
        overflowY: 'auto',
        p: 1.5,
        zIndex: (theme) => theme.zIndex.modal + 10,
      }}
    >
      {children}
    </Paper>,
    document.body
  );
}
